import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, ModeEditTwoToneIcon } from 'tuui';
import { HelpTooltip, INDICATION_TYPES, IndicationContent, TextCheckbox } from 'taboola-ultimate-ui';
import { CONVERSION_LOCATION } from 'modules/codeless-events/consts/conversionFlowConstants';
import { unescapeEmojis } from 'modules/codeless-events/utils/stringUtils';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { ConversionCategoryField } from 'modules/tracking/components/ConversionCategoryField/ConversionCategoryField';
import { TotalValueTooltip } from 'modules/tracking/components/TotalValueCheckBox/TotalValueTooltip/TotalValueTooltip';
import { CODELESS_CATEGORIES } from 'modules/tracking/consts/CodelessCategory';
import { STEP_IDS } from '../../config/stepIds';
import { useIncludeInTotalConversionHandler } from '../../hooks/useIncludeInTotalConversionHandler';
import { NewEventHeader } from '../Headers/NewEventHeader';
import { ConversionNameField } from './ConversionNameField';
import { MultiButtonMessage } from './MultiButtonMessage';
import styles from './setupClicksEventCategory.module.scss';

export const SetupClicksEventCategory = ({
    currentConversion,
    navigateToStepId,
    isValueFeatureEnabled,
    updateDraftEvent,
    handleSaveDraftToPersistent,
    setFocusedConversionId,
}) => {
    const isFetchedEvent = currentConversion?.eventName?.includes('tce');

    useEffect(() => {
        if (
            !isFetchedEvent &&
            currentConversion &&
            currentConversion.category &&
            currentConversion.trackedElements &&
            currentConversion.trackedElements.length > 0
        ) {
            const firstTrackedElement = currentConversion.trackedElements[0];
            if (!firstTrackedElement.displayText) {
                updateDraftEvent(draft => ({
                    ...draft,
                    trackedElements: currentConversion.trackedElements,
                }));
            }
        }
    }, [currentConversion, isFetchedEvent, updateDraftEvent]);

    const buttonNames = useMemo(() => {
        const trackedElements = currentConversion?.trackedElements || [];
        const displayTexts = trackedElements.map(element => unescapeEmojis(element.displayText));

        if (displayTexts.length === 0 || displayTexts.every(text => !text)) {
            if (!isFetchedEvent && currentConversion?.category) {
                return [currentConversion.category];
            }
            return [];
        }

        return displayTexts
            .map(text => {
                if (isFetchedEvent || text) {
                    return text;
                }
                return currentConversion?.category || '';
            })
            .filter(Boolean);
    }, [currentConversion, isFetchedEvent]);

    const conversionName = currentConversion?.conversionName;
    const handleConversionNameChange = useCallback(
        value => {
            updateDraftEvent(draft => ({
                ...draft,
                conversionName: value,
            }));
        },
        [updateDraftEvent]
    );

    const includeInTotalConversions = currentConversion?.includeInTotalConversions;
    const { handleSelectCheckbox, handleUnselectCheckbox } = useIncludeInTotalConversionHandler(updateDraftEvent);

    const handleCategoryChange = useCallback(
        categoryValue => {
            updateDraftEvent(draft => ({
                ...draft,
                category: categoryValue.value,
            }));
        },
        [updateDraftEvent]
    );

    const handleNextInternal = () => {
        if (isValueFeatureEnabled) {
            navigateToStepId(STEP_IDS.SETUP_VALUE);
            return;
        }

        handleSaveDraftToPersistent();
        setFocusedConversionId(undefined);
    };

    const handleBackInternal = () => {
        if (!navigateToStepId) {
            return;
        }

        const backStepId =
            currentConversion?.locationScope === CONVERSION_LOCATION.CROSS_SITE
                ? STEP_IDS.GLOBAL_BUTTON_SETUP
                : STEP_IDS.BUTTON_ANALYZER_STEP;
        navigateToStepId(backStepId);
    };

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={handleBackInternal} />
            <div className={styles['title-and-selector-container']}>
                <IndicationContent containerClassName={styles['container-indication']} type={INDICATION_TYPES.SUCCESS}>
                    <MultiButtonMessage
                        buttonNames={buttonNames}
                        locationScope={currentConversion?.locationScope}
                        crossSiteButtonType={currentConversion?.crossSiteButtonType}
                        trackedElements={currentConversion?.trackedElements}
                    />
                    <div className={styles['right-icon']} onClick={handleBackInternal}>
                        <ModeEditTwoToneIcon />
                    </div>
                </IndicationContent>

                <div className={styles['setup-title']}>
                    {currentConversion?.locationScope === CONVERSION_LOCATION.CROSS_SITE ? (
                        <FormattedMessage
                            id="codeless.conversion.global.setup.header.title"
                            defaultMessage="Track buttons across your site"
                        />
                    ) : (
                        <FormattedMessage
                            id="codeless.conversion.specific.setup.header.title"
                            defaultMessage="Setup specific page buttons"
                        />
                    )}
                </div>
                <ConversionNameField
                    conversionName={conversionName}
                    onConversionNameChange={handleConversionNameChange}
                />
                <ConversionCategoryField
                    field="codelessDraftEvent.category"
                    isCodeless={true}
                    onChange={handleCategoryChange}
                    skipValidations={false}
                    categories={CODELESS_CATEGORIES}
                    disabled={isFetchedEvent}
                />
            </div>
            <div className={styles['checkbox-container']}>
                <TextCheckbox
                    labelClassName={styles['checkbox-label']}
                    onSelectCheckbox={handleSelectCheckbox}
                    onUnselectCheckbox={handleUnselectCheckbox}
                    selectedValues={[includeInTotalConversions]}
                    independent
                    value
                    title={
                        <FormattedMessage
                            id="tracking.conversion.properties.total.conversion.checkbox.title"
                            defaultMessage="Include in total conversions"
                        />
                    }
                    data-metrics-component="Total value checkbox"
                    data-metrics-event-name={GTM_EVENTS.USABILITY}
                    data-metrics-value={includeInTotalConversions}
                />
                <HelpTooltip>
                    <TotalValueTooltip />
                </HelpTooltip>
            </div>

            <footer className={styles['footer']}>
                <Button
                    onClick={handleBackInternal}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={handleNextInternal}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                    disabled={!currentConversion?.category || !currentConversion?.conversionName}
                >
                    {isValueFeatureEnabled ? (
                        <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                    ) : (
                        <FormattedMessage id="codeless.create.event.button" defaultMessage="Create Event" />
                    )}
                </Button>
            </footer>
        </div>
    );
};
